/**
 * Bottom Tab Bar Component
 * Used across TAB-001, TAB-002, FAV-002
 * Token-only colors
 */

import { useCallback, useMemo } from 'react';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { useSafeLocation } from '@/hooks/useSafeLocation';
import { toast } from 'sonner';
import { Heart } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import ChatBadge from '@/components/features/ChatBadge';
import { AIMatchModal, useAIMatchModal, AI_MATCH_TOOLTIP_TEXT } from './AIMatchModal';
import { isAIProcessingEnabled } from '@/hooks/useUserSettings';
import { useUserContext } from '@/contexts/UserContext';
import { useAuthFeatureFlags } from '@/hooks/useAuthFeatureFlags';
// Import tab icons - active and inactive states
import friendsActiveIcon from '@/assets/friends_active.png';
import friendsInactiveIcon from '@/assets/friends_inactive.png';
import chatsActiveIcon from '@/assets/chats_active.png';
import chatsInactiveIcon from '@/assets/chats_inactive.png';
import heartActiveIcon from '@/assets/heart_active.png';
import heartInactiveIcon from '@/assets/heart_inactive.png';

const showComingSoon = () => {
  toast('Скоро появится', {
    description: 'Мы уже работаем над этим функционалом!',
  });
};

interface TabItem {
  id: string;
  label: string;
  activeIcon?: string;
  inactiveIcon?: string;
  route: string;
  isActive?: boolean;
  badge?: number;
  comingSoon?: boolean;
  customRender?: boolean;
}

interface TabBarProps {
  activeTab?: 'friends' | 'chats' | 'favorites' | 'ai_match';
  chatBadge?: number;
}

const TabBar = ({ chatBadge = 0 }: TabBarProps) => {
  const navigate = useSafeNavigate();
  const location = useSafeLocation();
  const { showModal, setShowModal, shouldShowModal } = useAIMatchModal();
  const { user } = useUserContext();
  const { showForYouTab } = useAuthFeatureFlags();

  const normalizedChatBadge =
    typeof chatBadge === 'number' && Number.isFinite(chatBadge) ? chatBadge : 0;
  
  // Auto-detect active tab based on current route
  const getActiveTab = (): 'friends' | 'chats' | 'favorites' | 'ai_match' => {
    if (location.pathname.startsWith('/favorites')) return 'favorites';
    if (location.pathname.startsWith('/chats')) return 'chats';
    // Check if we're on catalog with forYou filter
    if (location.pathname.startsWith('/catalog') && location.state?.forYou) return 'ai_match';
    if (location.pathname.startsWith('/catalog')) return 'friends';
    return 'friends';
  };
  
  const activeTab = getActiveTab();

  const tabs: TabItem[] = useMemo(() => {
    const baseTabs: TabItem[] = [
      {
        id: 'friends',
        label: 'Друзья',
        activeIcon: friendsActiveIcon,
        inactiveIcon: friendsInactiveIcon,
        route: '/catalog',
        isActive: activeTab === 'friends',
      },
      {
        id: 'chats',
        label: 'Чаты',
        activeIcon: chatsActiveIcon,
        inactiveIcon: chatsInactiveIcon,
        route: '/chats',
        isActive: activeTab === 'chats',
        badge: normalizedChatBadge,
        comingSoon: false, // Chat is now implemented
      },
    ];

    // Only add "Для вас" tab if feature flag is enabled
    if (showForYouTab) {
      baseTabs.push({
        id: 'ai_match',
        label: 'Для вас',
        route: '/catalog',
        isActive: activeTab === 'ai_match',
        customRender: true,
        comingSoon: false,
      });
    }

    baseTabs.push({
      id: 'favorites',
      label: 'Избранное',
      activeIcon: heartActiveIcon,
      inactiveIcon: heartInactiveIcon,
      route: '/favorites',
      isActive: activeTab === 'favorites',
      comingSoon: false,
    });

    return baseTabs;
  }, [activeTab, normalizedChatBadge, showForYouTab]);

  const handleAIMatchClick = useCallback(() => {
    // Check if AI processing is enabled in settings
    if (!isAIProcessingEnabled()) {
      toast.info('ИИ-обработка отключена в настройках', {
        description: 'Включите её в разделе Профиль → Настройки → ИИ-обработка',
      });
      return;
    }

    // If survey not completed OR modal was never shown, always open modal
    if (shouldShowModal()) {
      setShowModal(true);
      return;
    }

    // Navigate to catalog with "for you" filter and user's city
    navigate('/catalog', {
      state: {
        forYou: true,
        cityId: user?.geoPlaceId || null,
        cityName: user?.cityName || null,
      },
    });
  }, [navigate, user?.geoPlaceId, user?.cityName, shouldShowModal, setShowModal]);

  const handleTabPress = (tab: TabItem) => {
    // Special handling for AI match tab
    if (tab.id === 'ai_match') {
      handleAIMatchClick();
      return;
    }

    if (tab.comingSoon) {
      showComingSoon();
      return;
    }
    
    // For "friends" tab, navigate without forYou state to reset the mode
    if (tab.id === 'friends') {
      navigate('/catalog', { state: { forYou: false } });
      return;
    }
    
    if (location.pathname === tab.route) return;
    navigate(tab.route);
  };

  // Render AI match tab with custom icon
  const renderAIMatchTab = (tab: TabItem) => {
    const isActive = tab.isActive;
    
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              key={tab.id}
              onClick={() => handleTabPress(tab)}
              className="relative flex flex-col items-center justify-center min-w-[80px] min-h-[44px] py-1"
              style={{ fontFamily: '"CoFo Gothic", system-ui, sans-serif' }}
            >
              {/* Icon - Black heart outline when inactive, filled pink when active */}
              <div className="relative w-6 h-6 mb-0.5 flex items-center justify-center">
                <Heart 
                  size={22}
                  fill={isActive ? '#FF4081' : 'none'}
                  stroke={isActive ? '#FF4081' : '#3F2516'}
                  strokeWidth={2}
                />
              </div>
              {/* Label */}
              <span
                className={`text-[11px] font-medium ${
                  isActive ? 'text-text-accent-pink' : 'text-text-primary'
                }`}
              >
                {tab.label}
              </span>
            </button>
          </TooltipTrigger>
          <TooltipContent 
            side="top" 
            className="rounded-lg px-3 py-2"
            style={{ 
              backgroundColor: '#3F2516',
              color: 'white',
              fontFamily: 'CoFo Gothic, sans-serif',
              fontSize: '12px',
            }}
          >
            {AI_MATCH_TOOLTIP_TEXT}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <>
      <nav
        data-testid="tab-001-tabbar"
        className="fixed bottom-0 left-0 right-0 z-50 flex justify-center bg-bg-primary border-t border-border-tertiary"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <div className="w-full max-w-[420px] flex items-center justify-around h-[56px]">
          {tabs.map((tab) => {
            // Custom render for AI match tab
            if (tab.customRender) {
              return renderAIMatchTab(tab);
            }

            return (
              <button
                key={tab.id}
                onClick={() => handleTabPress(tab)}
                className="relative flex flex-col items-center justify-center min-w-[80px] min-h-[44px] py-1"
                style={{ fontFamily: '"CoFo Gothic", system-ui, sans-serif' }}
              >
                {/* Icon */}
                <div className="relative w-6 h-6 mb-0.5">
                  <img
                    src={tab.isActive ? tab.activeIcon : tab.inactiveIcon}
                    alt={tab.label}
                    className="w-full h-full object-contain"
                  />
                  {/* Badge */}
                  {(tab.badge ?? 0) > 0 && (
                    <ChatBadge
                      count={tab.badge ?? 0}
                      size="sm"
                      className="absolute -top-1 -right-2"
                    />
                  )}
                </div>
                {/* Label */}
                <span
                  className={`text-[11px] font-medium ${
                    tab.isActive ? 'text-text-accent-pink' : 'text-text-primary'
                  }`}
                >
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* AI Match Modal */}
      <AIMatchModal 
        open={showModal} 
        onOpenChange={setShowModal}
        onComplete={() => {
          // Navigate to catalog with "for you" filter and user's city after modal is dismissed
          navigate('/catalog', {
            state: {
              forYou: true,
              cityId: user?.geoPlaceId || null,
              cityName: user?.cityName || null,
            },
          });
        }}
      />
    </>
  );
};

export default TabBar;
